import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Avatar, Dropdown, Button, Breadcrumb, Modal, Form, Input, message } from 'antd';
import {
  DashboardOutlined, ThunderboltOutlined, SafetyOutlined,
  EnvironmentOutlined, FileTextOutlined, AlertOutlined,
  ApartmentOutlined, NodeIndexOutlined, FundOutlined,
  MoneyCollectOutlined, ToolOutlined, SettingOutlined,
  MenuFoldOutlined, MenuUnfoldOutlined, BulbOutlined,
  UserOutlined, LogoutOutlined, SwapOutlined, SolutionOutlined, CloudOutlined, MobileOutlined,
  ExperimentOutlined, TeamOutlined, ApiOutlined, RobotOutlined, KeyOutlined, LineChartOutlined,
  AuditOutlined, ControlOutlined, VideoCameraOutlined,
} from '@ant-design/icons';
import { useState, useEffect } from 'react';
import { auth } from '../services/api';
import IcpFooter from '../components/IcpFooter';

const { Header, Sider, Content } = Layout;

const breadcrumbMap = {
  '/dashboard': '总览仪表盘',
  '/monitoring': '实时监测',
  '/video-monitoring': '视频监控',
  '/video-calibration': '视频融合标定',
  '/photovoltaic': '光伏管理',
  '/storage': '储能管理',
  '/charging': '充电桩管理',
  '/energy': '能效分析',
  '/demand': '需量管理',
  '/strategy': '策略管理',
  '/carbon': '能碳管理',
  '/edge-collector': '边缘采集',
  '/digital-twin': '数字孪生',
  '/m': '运维小程序',
  '/alarms': '告警管理',
  '/work-orders': '运维工单',
  '/vpp/trade-loop': '交易闭环',
  '/vpp/quotation': 'AI智能报价',
  '/vpp/resources': '资源聚合',
  '/vpp/dispatch': '调度指令',
  '/vpp/market': '电力市场',
  '/vpp/demand': '需求响应',
  '/vpp/forecast': '预测中心',
  '/vpp/review': '交易复盘',
  '/vpp/settlement': '结算管理',
  '/vpp/customers': '客户合同',
  '/devices': '设备管理',
  '/reports': '报表中心',
  '/audit': '操作审计',
  '/tenants': '多租户管理',
  '/users': '用户管理',
};

const microgridKeys = ['/monitoring', '/video-monitoring', '/video-calibration', '/photovoltaic', '/storage', '/charging', '/energy', '/demand', '/strategy', '/carbon', '/edge-collector', '/digital-twin', '/m', '/alarms', '/work-orders'];

function getOpenKeys(pathKey) {
  if (pathKey.startsWith('/vpp')) return ['vpp'];
  if (microgridKeys.includes(pathKey)) return ['microgrid'];
  return [];
}

function buildMenuItems(role) {
  const baseMenu = [
    { key: '/dashboard', icon: <DashboardOutlined />, label: '总览仪表盘' },
    {
      key: 'microgrid', icon: <ThunderboltOutlined />, label: '园区微电网',
      children: [
        { key: '/monitoring', icon: <SafetyOutlined />, label: '实时监测' },
        { key: '/video-monitoring', icon: <VideoCameraOutlined />, label: '视频监控' },
        { key: '/video-calibration', icon: <VideoCameraOutlined />, label: '视频融合标定' },
        { key: '/photovoltaic', icon: <BulbOutlined />, label: '光伏管理' },
        { key: '/storage', icon: <EnvironmentOutlined />, label: '储能管理' },
        { key: '/charging', icon: <ThunderboltOutlined />, label: '充电桩管理' },
        { key: '/energy', icon: <FundOutlined />, label: '能效分析' },
        { key: '/demand', icon: <LineChartOutlined />, label: '需量管理' },
        { key: '/strategy', icon: <ControlOutlined />, label: '策略管理' },
        { key: '/carbon', icon: <CloudOutlined />, label: '能碳管理' },
        { key: '/edge-collector', icon: <NodeIndexOutlined />, label: '边缘采集' },
        { key: '/digital-twin', icon: <ExperimentOutlined />, label: '数字孪生' },
        { key: '/m', icon: <MobileOutlined />, label: '运维小程序' },
        { key: '/alarms', icon: <AlertOutlined />, label: '告警管理' },
        { key: '/work-orders', icon: <SolutionOutlined />, label: '运维工单' },
      ]
    },
    {
      key: 'vpp', icon: <ApartmentOutlined />, label: '虚拟电厂',
      children: [
        { key: '/vpp/trade-loop', icon: <SwapOutlined />, label: '交易闭环' },
        { key: '/vpp/quotation', icon: <RobotOutlined />, label: 'AI智能报价' },
        { key: '/vpp/resources', icon: <NodeIndexOutlined />, label: '资源聚合' },
        { key: '/vpp/dispatch', icon: <FileTextOutlined />, label: '调度指令' },
        { key: '/vpp/market', icon: <FundOutlined />, label: '电力市场' },
        { key: '/vpp/demand', icon: <AlertOutlined />, label: '需求响应' },
        { key: '/vpp/forecast', icon: <LineChartOutlined />, label: '预测中心' },
        { key: '/vpp/review', icon: <AuditOutlined />, label: '交易复盘' },
        { key: '/vpp/settlement', icon: <MoneyCollectOutlined />, label: '结算管理' },
        { key: '/vpp/customers', icon: <TeamOutlined />, label: '客户合同' },
      ]
    },
    { key: '/devices', icon: <ToolOutlined />, label: '设备管理' },
    { key: '/reports', icon: <FileTextOutlined />, label: '报表中心' },
  ];
  if (role === 'admin') {
    return [...baseMenu,
      { key: '/tenants', icon: <TeamOutlined />, label: '多租户管理' },
      { key: '/users', icon: <UserOutlined />, label: '用户管理' },
      { key: '/audit', icon: <SettingOutlined />, label: '操作审计' },
    ];
  }
  return baseMenu;
}

export default function MainLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdForm] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('qhy_user') || '{}');
  const menuItems = buildMenuItems(user.role);

  const selectedKey = '/' + location.pathname.split('/').slice(1).join('/');
  const [openKeys, setOpenKeys] = useState(() => getOpenKeys(selectedKey));

  useEffect(() => {
    setOpenKeys(getOpenKeys(selectedKey));
  }, [selectedKey]);

  const handleMenuClick = ({ key }) => {
    navigate(key);
  };

  const handleLogout = () => {
    localStorage.removeItem('qhy_token');
    localStorage.removeItem('qhy_user');
    navigate('/login');
  };

  // 修改密码：成功后提示并强制退出重新登录
  const handleChangePassword = async () => {
    try {
      const values = await pwdForm.validateFields();
      await auth.changePassword({ old_password: values.old_password, new_password: values.new_password });
      message.success('密码修改成功，请重新登录');
      setPwdOpen(false);
      pwdForm.resetFields();
      handleLogout();
    } catch (err) {
      if (err.message) message.error(err.message);
    }
  };

  const userMenu = {
    items: [
      { key: 'user', icon: <UserOutlined />, label: user.name || '用户', disabled: true },
      { type: 'divider' },
      { key: 'changePassword', icon: <KeyOutlined />, label: '修改密码', onClick: () => setPwdOpen(true) },
      { key: 'logout', icon: <LogoutOutlined />, label: '退出登录', danger: true, onClick: handleLogout },
    ],
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider trigger={null} collapsible collapsed={collapsed} theme="dark"
        style={{ overflow: 'auto', height: '100vh', position: 'fixed', left: 0, top: 0, bottom: 0 }}>
        <div style={{ height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: collapsed ? 14 : 18, fontWeight: 'bold', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          {collapsed ? '🏭' : '黔好运能源管理'}
        </div>
        <Menu theme="dark" mode="inline" selectedKeys={[selectedKey]} openKeys={openKeys}
          onOpenChange={setOpenKeys} items={menuItems} onClick={handleMenuClick} />
      </Sider>
      <Layout style={{ marginLeft: collapsed ? 80 : 200, transition: 'margin-left 0.2s' }}>
        <Header style={{ padding: '0 24px', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', position: 'sticky', top: 0, zIndex: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button type="text" icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)} style={{ fontSize: 16, width: 48, height: 48 }} />
            <Breadcrumb style={{ marginLeft: 16 }}>
              <Breadcrumb.Item>黔好运</Breadcrumb.Item>
              <Breadcrumb.Item>{breadcrumbMap[selectedKey] || '未知'}</Breadcrumb.Item>
            </Breadcrumb>
          </div>
          <Dropdown menu={userMenu} placement="bottomRight">
            <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
              <span>{user.name || '用户'}</span>
            </div>
          </Dropdown>
        </Header>
        <Content style={{ margin: 24, minHeight: 280 }}>
          <Outlet />
        </Content>
        <IcpFooter />
      </Layout>

      <Modal
        title="修改密码" open={pwdOpen}
        onCancel={() => { setPwdOpen(false); pwdForm.resetFields(); }}
        onOk={handleChangePassword} okText="确认修改"
      >
        <Form form={pwdForm} layout="vertical">
          <Form.Item name="old_password" label="旧密码" rules={[{ required: true, message: '请输入旧密码' }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="new_password" label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 8, message: '密码长度不能少于 8 位' },
              ({ getFieldValue }) => ({
                validator: (_, value) =>
                  !value || value !== getFieldValue('old_password')
                    ? Promise.resolve()
                    : Promise.reject(new Error('新密码不能与旧密码相同')),
              }),
            ]}
          >
            <Input.Password placeholder="至少 8 位" />
          </Form.Item>
          <Form.Item
            name="confirm_password" label="确认新密码" dependencies={['new_password']}
            rules={[
              { required: true, message: '请再次输入新密码' },
              ({ getFieldValue }) => ({
                validator: (_, value) =>
                  !value || value === getFieldValue('new_password')
                    ? Promise.resolve()
                    : Promise.reject(new Error('两次输入的密码不一致')),
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}
